import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import complianceReportsCustomizationService from '../../../services/complianceReportsCustomizationService';

const ViolationTrendsChart = ({ periodStart, periodEnd }) => {
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (periodStart && periodEnd) {
      loadTrends();
    }
  }, [periodStart, periodEnd]);

  const loadTrends = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await complianceReportsCustomizationService?.getViolationTrends(periodStart, periodEnd);
      setTrends(data);
    } catch (err) {
      setError(err?.message || 'Failed to load violation trends');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (direction) => {
    if (direction === 'decreasing') return { name: 'TrendingDown', color: 'text-success' };
    if (direction === 'increasing') return { name: 'TrendingUp', color: 'text-error' };
    return { name: 'Minus', color: 'text-muted-foreground' };
  };

  const formatCurrency = (amount) => {
    return `K${parseFloat(amount || 0)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-md p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-lg shadow-md p-6">
        <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md flex items-start gap-2">
          <Icon name="AlertCircle" size={18} className="mt-0.5" />
          <span className="text-sm">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
        <Icon name="TrendingUp" size={20} />
        Violation Trends Analysis
      </h3>

      {trends?.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="BarChart3" size={48} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">No violation trends data available for this period</p>
        </div>
      ) : (
        <div className="space-y-3">
          {trends?.map((trend) => {
            const trendIcon = getTrendIcon(trend?.trendDirection);
            return (
              <div
                key={trend?.id}
                className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-foreground capitalize">
                        {trend?.violationType?.replace(/_/g, ' ')}
                      </h4>
                      {trend?.department && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                          {trend?.department}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>{trend?.occurrenceCount} occurrences</span>
                      <span>•</span>
                      <span>{trend?.affectedEmployees} employees</span>
                      <span>•</span>
                      <span className="font-medium text-foreground">
                        {formatCurrency(trend?.totalCostKina)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name={trendIcon?.name} size={20} className={trendIcon?.color} />
                    {trend?.percentageChange !== null && (
                      <span className={`text-sm font-medium ${trendIcon?.color}`}>
                        {trend?.percentageChange > 0 ? '+' : ''}{trend?.percentageChange}%
                      </span>
                    )}
                  </div>
                </div>

                {trend?.severityDistribution && (
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-xs text-muted-foreground">Severity:</span>
                    <div className="flex items-center gap-2 text-xs">
                      {trend?.severityDistribution?.critical > 0 && (
                        <span className="bg-error/10 text-error px-2 py-0.5 rounded">
                          {trend?.severityDistribution?.critical} Critical
                        </span>
                      )}
                      {trend?.severityDistribution?.high > 0 && (
                        <span className="bg-warning/10 text-warning px-2 py-0.5 rounded">
                          {trend?.severityDistribution?.high} High
                        </span>
                      )}
                      {trend?.severityDistribution?.medium > 0 && (
                        <span className="bg-primary/10 text-primary px-2 py-0.5 rounded">
                          {trend?.severityDistribution?.medium} Medium
                        </span>
                      )}
                      {trend?.severityDistribution?.low > 0 && (
                        <span className="bg-muted text-muted-foreground px-2 py-0.5 rounded">
                          {trend?.severityDistribution?.low} Low
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ViolationTrendsChart;